<template>
  <div
    v-if="activeBanners.length > 0"
    class="banners-slider-container w-full lg:px-8 lg:pt-5"
  >
    <!-- Один баннер без управляющих элементов -->
    <div
      v-if="activeBanners.length === 1"
      class="single-banner"
    >
      <a
        :href="getBannerLink(activeBanners[0])"
        target="_blank"
        class="block w-full"
        @click="trackBannerClick(activeBanners[0])"
      >
        <picture>
          <source
            media="(min-width: 1024px)"
            :srcset="activeBanners[0].image1368Url"
          />
          <source
            media="(min-width: 440px)"
            :srcset="activeBanners[0].image1024Url"
          />
          <img
            :src="activeBanners[0].image440Url"
            :alt="activeBanners[0].name"
            class="w-full aspect-[11/9] sm:aspect-[16/7] lg:aspect-[3/1] object-contain lg:rounded-[28px]"
          />
        </picture>
      </a>
    </div>

    <!-- Слайдер с несколькими баннерами -->
    <div
      v-else
      class="multiple-banners relative"
    >
      <ScrollSnapSlider
        ref="slider"
        :items="activeBanners"
        slider-class="banners-swiper lg:rounded-[28px] lg:overflow-hidden"
        :get-item-key="(banner: any) => banner.id"
        :autoplay="{ delay: 5000 }"
        @slide-change="handleSlideChange"
      >
        <template #default="{ item: banner, index }">
          <div
            :class="{ 'hidden': !isHydrated && index > 0 }"
            class="w-full"
          >
            <a
              :href="getBannerLink(banner)"
              target="_blank"
              class="block w-full"
              @click="handleBannerClick(banner, $event)"
              @mousedown="handleMouseDown"
              @mouseup="handleMouseUp"
              @mousemove="handleMouseMove"
              @touchstart="handleTouchStart"
              @touchend="handleTouchEnd"
              @touchmove="handleTouchMove"
            >
              <picture>
                <source
                  media="(min-width: 1024px)"
                  :srcset="banner.image1368Url"
                />
                <source
                  media="(min-width: 440px)"
                  :srcset="banner.image1024Url"
                />
                <img
                  :src="banner.image440Url"
                  :alt="banner.name"
                  class="w-full aspect-[11/9] sm:aspect-[16/7] lg:aspect-[3/1] object-contain"
                />
              </picture>
            </a>
          </div>
        </template>
      </ScrollSnapSlider>

      <div class="hidden lg:block">
        <button
          class="swiper-button-prev absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/20 hover:bg-white/40 active:bg-white/30 rounded-full flex items-center justify-center z-10 shadow-lg transition-all border border-white"
          aria-label="Предыдущий баннер"
          @click="slidePrev"
        >
          <ArrowSvg class="text-white" />
        </button>
        <button
          class="swiper-button-next absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/20 hover:bg-white/40 active:bg-white/30 rounded-full flex items-center justify-center z-10 shadow-lg transition-all border border-white"
          aria-label="Следующий баннер"
          @click="slideNext"
        >
          <ArrowSvg class="rotate-180 text-white" />
        </button>
      </div>

      <div
        class="swiper-pagination flex justify-center gap-2 lg:gap-2.5 absolute bottom-3 left-1/2 -translate-x-1/2 z-10 lg:static lg:mt-5 lg:translate-x-0"
      >
        <button
          v-for="(banner, index) in activeBanners"
          :key="`dot-${banner.id}`"
          :class="[
            'lg:w-2.5 lg:h-2.5 w-2 h-2 rounded-full transition-all duration-300',
            currentSlide === index ? 'bg-[#FFC310]' : 'bg-[#D9D9D9]'
          ]"
          :aria-label="`Перейти к баннеру ${index + 1}`"
          :aria-current="currentSlide === index ? 'true' : 'false'"
          @click="slideTo(index)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import ArrowSvg from '~/assets/svg/ui/arrow.svg?component'
import ScrollSnapSlider from '~/components/shared/scroll_snap_slider.vue'

// Local state
const slider = ref(null)
const currentSlide = ref(0)
const isHydrated = ref(false)
const isDragging = ref(false)
const dragStartX = ref(0)

const { $tracking } = useNuxtApp()
const { city } = useSearchForm()
const { getBannersByCity, fetchBanners } = useBanners()

const banners = computed(() => getBannersByCity(city.value?.id))

// Computed
const activeBanners = computed(() => {
  if (!Array.isArray(banners.value)) {
    return []
  }
  return banners.value
    .filter(
      banner =>
        banner &&
          banner.active &&
          banner.image440Url &&
          banner.image1024Url &&
          banner.image1368Url,
    )
    .sort((a, b) => a.order - b.order)
})

// Methods
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getBannerLink = (banner: any) => {
  // eslint-disable-next-line no-undef
  const url = new URL(banner.link)
  url.searchParams.set('banner_id', banner.id)
  return url.toString()
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const trackBannerClick = (banner: any) => {
  // Отправляем событие в аналитику
  $tracking?.logEventAmplitude('banner click', {
    banner_id: banner.id,
    banner_name: banner.name,
    banner_link: banner.link,
    city_id: city.value?.id,
    city_name: city.value?.name,
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleBannerClick = (banner: any, event: any) => {
  // Если был свайп, предотвращаем переход по ссылке
  if (isDragging.value) {
    event.preventDefault()
    return
  }

  // Если это обычный клик, разрешаем переход
  trackBannerClick(banner)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleMouseDown = (event: any) => {
  isDragging.value = false
  dragStartX.value = event.clientX
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleMouseMove = (event: any) => {
  if (dragStartX.value !== 0) {
    const deltaX = Math.abs(event.clientX - dragStartX.value)
    // Если мышь переместилась больше чем на 5 пикселей, считаем это свайпом
    if (deltaX > 5) {
      isDragging.value = true
    }
  }
}

const handleMouseUp = () => {
  // Сбрасываем состояние свайпа через небольшую задержку
  // eslint-disable-next-line no-undef
  setTimeout(() => {
    isDragging.value = false
    dragStartX.value = 0
  }, 100)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTouchStart = (event: any) => {
  isDragging.value = false
  if (event.touches.length > 0) {
    dragStartX.value = event.touches[0].clientX
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTouchMove = (event: any) => {
  if (dragStartX.value !== 0 && event.touches.length > 0) {
    const deltaX = Math.abs(event.touches[0].clientX - dragStartX.value)
    // Если палец переместился больше чем на 5 пикселей, считаем это свайпом
    if (deltaX > 5) {
      isDragging.value = true
    }
  }
}

const handleTouchEnd = () => {
  // Сбрасываем состояние свайпа через небольшую задержку
  // eslint-disable-next-line no-undef
  setTimeout(() => {
    isDragging.value = false
    dragStartX.value = 0
  }, 100)
}

const slidePrev = () => {
  if (slider.value) {
    slider.value.slidePrev()
  }
}

const slideNext = () => {
  if (slider.value) {
    slider.value.slideNext()
  }
}

const slideTo = (index: number) => {
  if (slider.value) {
    slider.value.slideTo(index)
  }
}

// Event handlers for ScrollSnapSlider
const handleSlideChange = (index: number) => {
  currentSlide.value = index
}

const loadBanners = async () => {
  if (!city.value?.id) return
  try {
    await fetchBanners({ cityId: city.value.id })
  } catch {
    // Ошибка при загрузке баннеров
    // В продакшене ошибки логируются автоматически
  }
}

// Watchers
watch(city, () => {
  loadBanners()
})

// Lifecycle
onMounted(() => {
  if (import.meta.client) {
    isHydrated.value = true
  }
})
</script>

<style lang="sass" scoped>
  .banners-slider-container
    @apply w-full

  .single-banner,
  .multiple-banners
    @apply relative overflow-hidden

  :deep(.banners-swiper)
    @apply w-full

  :deep(.scroll-snap-slide)
    @apply w-full

  // Адаптивные размеры
  @media (max-width: 439px)
    .single-banner img,
    .multiple-banners img
      @apply aspect-[11/9]

  @media (min-width: 440px) and (max-width: 1023px)
    .single-banner img,
    .multiple-banners img
      @apply aspect-[16/7]

  @media (min-width: 1024px)
    .single-banner img,
    .multiple-banners img
      @apply aspect-[3/1]

    // На десктопе точки выносим наружу
    .swiper-pagination
      @apply static mt-5 translate-x-0
</style>
